import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { App as AntdApp } from 'antd';

import { GraphApi } from '@/services/api/graphApi';
import type { CanvasUploadResult, UploadFileResponse } from '@/types/graph';

import type { UploadBatch } from './types';
import {
  type StartBatchOptions,
  UploadCenterContext,
  type UploadCenterContextValue,
} from './UploadCenterContext';
import { loadBatches, saveBatches } from './uploadStateStorage';

/** assetType 数字到资产 Tab key 的映射 */
const ASSET_TYPE_TO_TAB_KEY: Record<number, string> = {
  2: 'character',
  3: 'scene',
  4: 'prop',
  9: 'audio',
  10: 'other',
};

/** 将技术性上传错误信息转换为用户可理解的友好文案 */
function toFriendlyUploadError(error: Error): string {
  const message = error.message || '';
  if (/413|too large|payload/i.test(message)) return '文件过大，请减小文件体积后重试';
  if (/415|unsupported|media type/i.test(message)) return '文件格式不支持，请检查后重试';
  if (/401|unauthorized/i.test(message)) return '登录已过期，请重新登录后重试';
  if (/403|forbidden/i.test(message)) return '没有上传权限，请联系管理员';
  if (/404|not found/i.test(message)) return '上传地址不存在，请刷新页面后重试';
  if (/timeout|timed? ?out/i.test(message)) return '上传超时，请检查网络后重试';
  if (/network|net::err|fetch|connection|断网/i.test(message))
    return '网络连接异常，请检查网络后重试';
  if (/5\d{2}|server|internal/i.test(message)) return '服务器异常，请稍后重试';
  return message || '上传失败，请稍后重试';
}

interface UploadCenterProviderProps {
  children: React.ReactNode;
}

const SINGLE_FILE_UPLOAD_CONCURRENCY = 3;

function genBatchId(): string {
  // 16 位随机 ID，足够避免单页面会话内的碰撞
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * 全局上传中心 Provider。
 *
 * 职责：
 * 1. 维护批次状态（uploading / success / failed / cancelled）
 * 2. 调用对应上传原语，桥接 xhr 进度到批次 progress
 * 3. 已完成的批次持久化到 localStorage（便于跨页面/刷新查看）
 *
 * 注意：该 Provider 不渲染上传进度，业务侧通过内嵌进度组件消费。
 */
export const UploadCenterProvider: React.FC<UploadCenterProviderProps> = ({ children }) => {
  const { notification } = AntdApp.useApp();
  const [batches, setBatches] = useState<UploadBatch[]>(() => loadBatches());
  // 运行时内存：batchId → xhr 集合；视频批次会并发多个请求。
  const xhrMapRef = useRef<Map<string, Set<XMLHttpRequest>>>(new Map());
  const cancelledBatchIdsRef = useRef<Set<string>>(new Set());
  const notificationRef = useRef(notification);
  notificationRef.current = notification;

  // 持久化：仅在 batches 变化时写一次
  useEffect(() => {
    saveBatches(batches);
  }, [batches]);

  const [recentUploadNamesMap, setRecentUploadNamesMap] = useState<Record<string, string[]>>({});
  const addRecentUploadNames = useCallback((tabKey: string, names: string[]) => {
    setRecentUploadNamesMap((prev) => ({
      ...prev,
      [tabKey]: [...(prev[tabKey] ?? []), ...names],
    }));
  }, []);

  const updateBatch = useCallback((batchId: string, patch: Partial<UploadBatch>) => {
    setBatches((prev) => prev.map((b) => (b.id === batchId ? { ...b, ...patch } : b)));
  }, []);

  const startBatch = useCallback(
    (options: StartBatchOptions): string => {
      const batchId = genBatchId();
      const totalBytes = options.files.reduce((sum, f) => sum + f.size, 0);
      const fileNames = options.files.map((file) => file.name.replace(/\.[^.]+$/, ''));
      const newBatch: UploadBatch = {
        id: batchId,
        projectKey: options.projectKey,
        projectName: options.projectName,
        assetType: options.assetType,
        assetTypeLabel: options.assetTypeLabel,
        mode: options.mode,
        source: options.source,
        fileNames,
        fileCount: options.files.length,
        totalBytes,
        progress: 0,
        stage: 'transferring',
        status: 'uploading',
        createdAt: Date.now(),
      };
      setBatches((prev) => [...prev, newBatch]);

      // 异步发起请求；不阻塞 startBatch 返回
      void (async () => {
        let successCount = 0;
        let diagnosticDescription: React.ReactNode;
        try {
          const xhrSet = new Set<XMLHttpRequest>();
          xhrMapRef.current.set(batchId, xhrSet);
          let result: CanvasUploadResult[] = [];
          let diagnosticCount = 0;
          if (options.mode === 'canvas') {
            const response = await GraphApi.uploadCanvasFiles(
              options.projectKey,
              options.source === 'zip' ? 'zip' : 'files',
              options.files,
              {
                registerXhr: (xhr) => xhrSet.add(xhr),
                onProgress: (percent) =>
                  updateBatch(batchId, {
                    progress: percent,
                    stage: percent >= 99 ? 'processing' : 'transferring',
                  }),
              },
              options.scope,
            );
            successCount = response.node_patch.created_nodes.length;
            diagnosticCount = response.diagnostics.length;
            if (diagnosticCount > 0) {
              const failedCount = response.diagnostics.filter(
                (item) => item.phase === 'failed',
              ).length;
              diagnosticDescription = (
                <div>
                  <p>
                    已上传 {successCount} 个素材，{failedCount} 个失败，
                    {diagnosticCount - failedCount} 个已跳过
                  </p>
                  <details className="mt-2">
                    <summary className="cursor-pointer">查看详情</summary>
                    <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto break-words">
                      {response.diagnostics.map((item, index) => (
                        <li key={`${item.source_path}:${index}`}>
                          {item.source_path}：{item.phase === 'failed' ? '失败' : '已跳过'}，
                          {item.message}
                        </li>
                      ))}
                    </ul>
                  </details>
                </div>
              );
            }
            await options.onNodePatch?.(response.node_patch);
            options.onDiagnostics?.(response.diagnostics);
            if (successCount === 0 && diagnosticCount > 0) {
              throw new Error(
                response.diagnostics
                  .map((diagnostic) => `${diagnostic.source_path}：${diagnostic.message}`)
                  .join('；'),
              );
            }
          } else if (options.mode === 'video' || options.mode === 'text') {
            const progresses = options.files.map(() => 0);
            const responses: Array<UploadFileResponse | undefined> = [];
            const errors: string[] = [];
            let nextIndex = 0;
            const workers = Array.from(
              { length: Math.min(SINGLE_FILE_UPLOAD_CONCURRENCY, options.files.length) },
              async () => {
                while (!cancelledBatchIdsRef.current.has(batchId)) {
                  const index = nextIndex;
                  nextIndex += 1;
                  const file = options.files[index];
                  if (!file) return;
                  try {
                    const progressOptions = {
                      registerXhr: (xhr: XMLHttpRequest) => xhrSet.add(xhr),
                      onProgress: (percent: number) => {
                        progresses[index] = percent;
                        const uploadedBytes = progresses.reduce(
                          (sum, progress, fileIndex) =>
                            sum + options.files[fileIndex].size * progress,
                          0,
                        );
                        updateBatch(batchId, {
                          progress:
                            totalBytes > 0
                              ? Math.round(uploadedBytes / totalBytes)
                              : Math.round(
                                  progresses.reduce((sum, progress) => sum + progress, 0) /
                                    progresses.length,
                                ),
                        });
                      },
                    };
                    responses[index] =
                      options.mode === 'video'
                        ? await GraphApi.uploadVideo(
                            options.projectKey,
                            file,
                            progressOptions,
                            options.scope,
                          )
                        : await GraphApi.uploadText(
                            options.projectKey,
                            file,
                            progressOptions,
                            options.scope,
                          );
                    progresses[index] = 100;
                  } catch (error) {
                    const uploadError =
                      error instanceof Error ? error : new Error(String(error ?? '上传失败'));
                    errors.push(`${file.name}：${toFriendlyUploadError(uploadError)}`);
                  }
                }
              },
            );
            await Promise.all(workers);
            if (cancelledBatchIdsRef.current.has(batchId)) {
              throw new Error('上传已取消');
            }
            result = responses.flatMap((response) =>
              response?.node && response.action_record
                ? [{ node: response.node, action_record: response.action_record }]
                : [],
            );
            successCount = result.length;
            if (result.length > 0) await options.onUploaded?.(result);
            if (errors.length > 0) {
              throw new Error(errors.join('；'));
            }
          } else {
            result = await GraphApi.uploadBatchAssets(
              options.projectKey,
              options.assetType,
              options.files,
              {
                registerXhr: (xhr) => xhrSet.add(xhr),
                onProgress: (percent) => updateBatch(batchId, { progress: percent }),
              },
              options.scope,
            );
            successCount = result.length;
            await options.onUploaded?.(result);
          }
          xhrMapRef.current.delete(batchId);
          cancelledBatchIdsRef.current.delete(batchId);
          updateBatch(batchId, {
            status: 'success',
            progress: 100,
            successCount,
            finishedAt: Date.now(),
          });
          // 自动写入红点数据（无论在弹窗内还是会话场景上传都生效）
          const uploadedTabKey = ASSET_TYPE_TO_TAB_KEY[options.assetType];
          if (uploadedTabKey) {
            const uploadedFileNames = options.files.map((file) =>
              file.name.replace(/\.[^.]+$/, ''),
            );
            addRecentUploadNames(uploadedTabKey, uploadedFileNames);
          }
          // 非弹窗场景下弹出成功通知
          if (!dockSuppressedRef.current) {
            const label = options.assetTypeLabel ?? '文件';
            const assetTabKey = ASSET_TYPE_TO_TAB_KEY[options.assetType];
            notificationRef.current[diagnosticCount > 0 ? 'warning' : 'success']({
              message: diagnosticCount > 0 ? '部分素材未上传' : '上传完成',
              description:
                diagnosticDescription ??
                (options.mode === 'canvas'
                  ? `已上传 ${successCount} 个素材`
                  : `${label}上传成功，共 ${successCount} 个文件`),
              placement: 'topRight',
              duration: diagnosticCount > 0 ? 0 : 4,
              style: {
                background: 'rgb(var(--color-dark-card))',
                border: '1px solid rgb(var(--color-dark-border))',
                color: 'rgb(var(--color-dark-text))',
                cursor: options.mode === 'canvas' ? 'default' : 'pointer',
              },
              onClick:
                options.mode === 'canvas'
                  ? undefined
                  : () => {
                      openProgressHandlerRef.current?.(assetTabKey);
                      notificationRef.current.destroy();
                    },
            });
          }
          options.onSuccess?.({ successCount, batchId });
        } catch (rawError) {
          xhrMapRef.current.delete(batchId);
          const error =
            rawError instanceof Error ? rawError : new Error(String(rawError ?? '上传失败'));
          // 区分用户主动取消 vs 真实失败
          const isCancelled = error.message === '上传已取消';
          updateBatch(batchId, {
            status: isCancelled ? 'cancelled' : 'failed',
            errorMessage: error.message,
            successCount,
            finishedAt: Date.now(),
          });
          // 非弹窗场景下弹出失败通知（用户主动取消不提示）
          if (!isCancelled && !dockSuppressedRef.current) {
            const label = options.assetTypeLabel ?? '文件';
            const assetTabKey = ASSET_TYPE_TO_TAB_KEY[options.assetType];
            const friendlyMessage = toFriendlyUploadError(error);
            notificationRef.current.error({
              message:
                options.mode === 'canvas' && successCount > 0
                  ? '素材已上传，画布更新失败'
                  : diagnosticDescription
                    ? '未上传任何素材'
                    : '上传失败',
              description: diagnosticDescription ?? `${label}上传失败：${friendlyMessage}`,
              placement: 'topRight',
              duration: diagnosticDescription ? 0 : 6,
              style: {
                background: 'rgb(var(--color-dark-card))',
                border: '1px solid rgb(var(--color-dark-border))',
                color: 'rgb(var(--color-dark-text))',
                cursor: options.mode === 'canvas' ? 'default' : 'pointer',
              },
              onClick:
                options.mode === 'canvas'
                  ? undefined
                  : () => {
                      openProgressHandlerRef.current?.(assetTabKey);
                      notificationRef.current.destroy();
                    },
            });
          }
          options.onError?.(error, batchId);
        }
      })();

      return batchId;
    },
    [addRecentUploadNames, updateBatch],
  );

  const cancelBatch = useCallback((batchId: string) => {
    cancelledBatchIdsRef.current.add(batchId);
    const xhrs = xhrMapRef.current.get(batchId);
    if (xhrs) {
      xhrs.forEach((xhr) => xhr.abort());
      // abort 触发的 reject 已由 startBatch 内的 catch 改为 cancelled 状态
    }
  }, []);

  const removeBatch = useCallback((batchId: string) => {
    setBatches((prev) => prev.filter((b) => b.id !== batchId));
  }, []);

  const clearFinished = useCallback(() => {
    setBatches((prev) => prev.filter((b) => b.status === 'uploading' || b.status === 'pending'));
  }, []);

  const hasUploading = useMemo(() => batches.some((b) => b.status === 'uploading'), [batches]);

  // 当内嵌进度组件可见时（如知识库弹窗打开），抑制全局浮窗
  const [dockSuppressed, setDockSuppressed] = useState(false);
  const dockSuppressedRef = useRef(dockSuppressed);
  dockSuppressedRef.current = dockSuppressed;
  const setSuppressDock = useCallback((suppressed: boolean) => {
    setDockSuppressed(suppressed);
  }, []);

  // ── 红点数据：记录各 Tab 最近上传成功的文件名，跨弹窗生命周期持久化 ──
  const removeRecentUploadName = useCallback((tabKey: string, name: string) => {
    setRecentUploadNamesMap((prev) => {
      const tabNames = prev[tabKey];
      if (!tabNames) return prev;
      const filtered = tabNames.filter((n) => n !== name);
      if (filtered.length === 0) {
        const next = { ...prev };
        delete next[tabKey];
        return next;
      }
      return { ...prev, [tabKey]: filtered };
    });
  }, []);
  const clearRecentUploadNamesForTabs = useCallback((tabKeys: string[]) => {
    setRecentUploadNamesMap((prev) => {
      const next = { ...prev };
      for (const key of tabKeys) {
        delete next[key];
      }
      return next;
    });
  }, []);
  const clearAllRecentUploadNames = useCallback(() => {
    setRecentUploadNamesMap({});
  }, []);

  // 业务层注册的"打开上传进度界面"回调（如打开知识库弹窗），可接收 assetTabKey 定位到对应 Tab
  const openProgressHandlerRef = useRef<((assetTabKey?: string) => void) | null>(null);
  const registerOpenProgressHandler = useCallback((handler: (assetTabKey?: string) => void) => {
    openProgressHandlerRef.current = handler;
  }, []);
  const unregisterOpenProgressHandler = useCallback(() => {
    openProgressHandlerRef.current = null;
  }, []);

  const value: UploadCenterContextValue = useMemo(
    () => ({
      batches,
      startBatch,
      cancelBatch,
      removeBatch,
      clearFinished,
      hasUploading,
      dockSuppressed,
      setSuppressDock,
      registerOpenProgressHandler,
      unregisterOpenProgressHandler,
      recentUploadNamesMap,
      addRecentUploadNames,
      removeRecentUploadName,
      clearRecentUploadNamesForTabs,
      clearAllRecentUploadNames,
    }),
    [
      batches,
      startBatch,
      cancelBatch,
      removeBatch,
      clearFinished,
      hasUploading,
      dockSuppressed,
      setSuppressDock,
      registerOpenProgressHandler,
      unregisterOpenProgressHandler,
      recentUploadNamesMap,
      addRecentUploadNames,
      removeRecentUploadName,
      clearRecentUploadNamesForTabs,
      clearAllRecentUploadNames,
    ],
  );

  return <UploadCenterContext.Provider value={value}>{children}</UploadCenterContext.Provider>;
};
